import { useState } from "react";
import { toast } from "sonner";
import { Plus, KeyRound, Eye, EyeOff, Copy, Pencil, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, copyText, errMsg } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { Panel, EmptyState } from "@/components/core/AppLayout";
import { ConfirmDialog } from "@/components/core/ConfirmDialog";
import { AccessFormDialog } from "./AccessFormDialog";

const IconBtn = ({ onClick, children, testid, title, danger }) => (
  <button type="button" title={title} onClick={onClick} data-testid={testid}
    className={`p-1.5 rounded-lg transition-colors ${danger ? "text-[#E8173A] hover:bg-red-50" : "text-[#0A1128] hover:bg-[#A6DAFC]"}`}>{children}</button>
);

const AccessCard = ({ a, onEdit, onDelete }) => {
  const [pwd, setPwd] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchPwd = async () => {
    setLoading(true);
    try { const r = await api.get(`/accessi/${a.id}/password`); return r.data.password; }
    finally { setLoading(false); }
  };
  const toggle = async () => {
    if (pwd !== null) return setPwd(null);
    try { setPwd(await fetchPwd()); } catch (e) { toast.error(errMsg(e)); }
  };
  const copyPwd = async () => {
    try { await copyText(pwd ?? (await fetchPwd())); toast.success("Password copiata"); } catch (e) { toast.error(errMsg(e)); }
  };
  const copyUser = async () => { await copyText(a.username); toast.success("Username copiato"); };

  return (
    <Panel className="p-5 group" data-testid={`access-card-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-[#0A1128] text-white flex items-center justify-center shrink-0"><KeyRound className="h-4 w-4" /></div>
          <div className="min-w-0">
            <div className="font-display font-extrabold text-[#0A1128] truncate" data-testid={`access-nome-${a.id}`}>{a.nome}</div>
            {a.url && <a href={a.url} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-[#E8173A] inline-flex items-center gap-1 truncate max-w-full" data-testid={`access-url-${a.id}`}>{a.url}<ExternalLink className="h-3 w-3 shrink-0" /></a>}
          </div>
        </div>
        <div className="flex opacity-60 group-hover:opacity-100 transition-opacity">
          <IconBtn onClick={() => onEdit(a)} testid={`access-edit-${a.id}`} title="Modifica"><Pencil className="h-4 w-4" /></IconBtn>
          <IconBtn onClick={() => onDelete(a)} testid={`access-delete-${a.id}`} title="Elimina" danger><Trash2 className="h-4 w-4" /></IconBtn>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
          <div className="min-w-0"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Username / email</div>
            <div className="text-sm font-semibold text-slate-800 truncate" data-testid={`access-username-${a.id}`}>{a.username || "—"}</div></div>
          {a.username && <IconBtn onClick={copyUser} testid={`access-copy-username-${a.id}`} title="Copia username"><Copy className="h-4 w-4" /></IconBtn>}
        </div>
        <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
          <div className="min-w-0"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Password</div>
            <div className="text-sm font-mono font-semibold text-slate-800 truncate" data-testid={`access-password-${a.id}`}>{a.has_password ? (pwd ?? "••••••••••") : "—"}</div></div>
          {a.has_password && (
            <div className="flex">
              <IconBtn onClick={toggle} testid={`access-toggle-password-${a.id}`} title={pwd !== null ? "Nascondi" : "Mostra"}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : pwd !== null ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </IconBtn>
              <IconBtn onClick={copyPwd} testid={`access-copy-password-${a.id}`} title="Copia password"><Copy className="h-4 w-4" /></IconBtn>
            </div>
          )}
        </div>
      </div>
      {a.note && <div className="text-xs text-slate-500 mt-3 whitespace-pre-wrap" data-testid={`access-note-${a.id}`}>{a.note}</div>}
    </Panel>
  );
};

export const ClientAccessi = ({ clientId }) => {
  const { data: items, reload } = useFetch(`/clients/${clientId}/accessi`);
  const [form, setForm] = useState({ open: false, item: null });
  const [del, setDel] = useState(null);

  const remove = async () => {
    try { await api.delete(`/accessi/${del.id}`); toast.success("Accesso eliminato"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  return (
    <div data-testid="client-accessi">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-slate-500">Credenziali dei portali del cliente. Le password sono cifrate.</div>
        <Button onClick={() => setForm({ open: true, item: null })} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="add-access-button"><Plus className="h-4 w-4 mr-1" />Nuovo accesso</Button>
      </div>
      {items && !items.length ? (
        <Panel><EmptyState icon={KeyRound} title="Nessun accesso salvato" text="Aggiungi le credenziali di social, CMS e piattaforme del cliente." testid="accessi-empty" /></Panel>
      ) : (
        <div className="grid grid-cols-3 gap-5">
          {(items || []).map((a) => <AccessCard key={a.id} a={a} onEdit={(item) => setForm({ open: true, item })} onDelete={setDel} />)}
        </div>
      )}
      <AccessFormDialog open={form.open} onOpenChange={(o) => setForm((p) => ({ ...p, open: o }))} item={form.item} clientId={clientId} onSaved={reload} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare l'accesso?" description={`Le credenziali di «${del?.nome}» verranno eliminate definitivamente.`} onConfirm={remove} />
    </div>
  );
};
